'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Theme } from '@/common/enums';

interface ScopedThemeContextValue {
  theme: string;
  resolvedTheme: string;
  systemTheme: 'dark' | 'light';
  setTheme: (theme: string) => void;
  themes: string[];
}

const ScopedThemeContext = createContext<ScopedThemeContextValue | null>(null);

/**
 * Provides a scoped theme context for use in `StandaloneWrapper`.
 *
 * The consumer controls the theme via the `theme` prop.
 * When the host app changes theme, it simply passes the new value —
 * no DOM observation or MutationObserver needed.
 *
 * The provider applies `dark`/`light` class + `color-scheme` on the
 * container element so Tailwind `dark:` variants and native browser
 * styles work correctly.
 */
export function ScopedThemeProvider({
  children,
  containerRef,
  theme: themeProp = Theme.Light,
}: PropsWithChildren<{
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** The theme to apply: 'light', 'dark', or 'system'. */
  theme?: string;
}>) {
  const themes = useMemo(() => [Theme.Light, Theme.Dark, Theme.System], []);

  // Detect system preference
  const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme =
    themeProp === Theme.System ? systemTheme : themeProp === Theme.Dark ? 'dark' : 'light';

  // setTheme is a no-op in standalone mode — the host controls the theme via prop.
  const setTheme = useCallback((_newTheme: string) => {
    // Theme is controlled via prop; this is provided for API compatibility
    // with the useTheme() hook used throughout the component tree.
  }, []);

  // Apply class to container element
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.classList.remove('dark', 'light');
    el.classList.add(resolvedTheme);
    el.style.colorScheme = resolvedTheme;
  }, [resolvedTheme, containerRef]);

  const value = useMemo(
    () => ({
      theme: themeProp,
      resolvedTheme,
      systemTheme,
      setTheme,
      themes,
    }),
    [themeProp, resolvedTheme, systemTheme, setTheme, themes],
  );

  return <ScopedThemeContext.Provider value={value}>{children}</ScopedThemeContext.Provider>;
}

/**
 * Returns scoped theme context, or `null` if outside a `ScopedThemeProvider`.
 */
export function useScopedTheme(): ScopedThemeContextValue | null {
  return useContext(ScopedThemeContext);
}
